import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState, useRef } from "react";
import { supabase } from "@/lib/supabase";
import type { Session } from "@supabase/supabase-js";
import { syncQueuedMutations, getOfflineQueue } from "@/lib/offlineSync";

interface AppUser {
  id: string;
  email: string | null;
  firstName: string | null;
  lastName: string | null;
  role: string;
  subscriptionStatus: string;
  trialEndsAt: string | null;
  subscriptionEndsAt: string | null;
  weightUnit: string;
  distanceUnit: string;
  musicGenre: string;
  avatarId: string | null;
  experienceLevel: string | null;
  hiddenCuratedGenres: string[] | null;
  customPlaylists: any[] | null;
}

// Check if user has active subscription or trial access
export function hasActiveAccess(user: AppUser | null): boolean {
  if (!user) return false;
  
  const status = user.subscriptionStatus;
  const extendedUser = user as any;
  
  // Active subscription always has access
  if (status === 'active') {
    return true;
  }
  
  // Privileged roles always have access: admin, beta, coach, subscriber
  const role = user.role;
  if (role === 'admin' || role === 'beta' || role === 'coach' || role === 'subscriber') {
    return true;
  }
  
  // Check isCoach property (for users who are coaches but may have a different role)
  if (extendedUser.isCoach === true) {
    return true;
  }
  
  // Trial access - check if trial is still valid
  if (status === 'trial' && user.trialEndsAt) {
    const trialEnd = new Date(user.trialEndsAt);
    return trialEnd > new Date();
  }
  
  return false;
}

// Consolidated list of user-specific localStorage keys to clear on sign out
const USER_LOCAL_STORAGE_KEYS = [
  'programMemberships',
  'myPrograms',
  'favoritePrograms',
  'customPrograms',
  'myFitBeats_global_calendar',
  'myFitBeats_scheduled_sessions',
  'myFitBeats_favorites',
  'myFitBeats_weight_logs',
  'myFitBeats_rest_timers',
  'myFitBeats_demo_initialized',
  'myFitBeats_hidden_blocks',
  'myFitBeats_selectedPlaylistId',
  'myFitBeats_selectedPlaylist',
  'myFitBeats_showFavorites',
  'myFitBeats_showOtherPrograms',
  'myFitBeats_hide_upcoming_blocks',
  'myFitBeats_1rmLogs',
  'myFitBeats_trackedLifts',
  'myFitBeats_currentUserId',
  'programDrafts',
  'myFitBeats_cachedUser',
] as const;

// Helper to clear all user-specific localStorage keys
function clearUserLocalStorageKeys() {
  USER_LOCAL_STORAGE_KEYS.forEach(key => localStorage.removeItem(key));
  
  const progressKeys = Object.keys(localStorage).filter(key => 
    key.startsWith('programProgress_') || 
    key.startsWith('workout_') ||
    key.startsWith('exerciseProgress_')
  );
  progressKeys.forEach(key => localStorage.removeItem(key));
}

// Flag to track if we should skip localStorage clearing on sign out
// Used when sync fails and we want to preserve data for next login
let skipLocalStorageClearOnSignOut = false;

export function useAuth() {
  const queryClient = useQueryClient();
  const [session, setSession] = useState<Session | null>(null);
  const [initializing, setInitializing] = useState(true);
  const initializedRef = useRef(false);
  const previousUserIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!supabase) {
      setInitializing(false);
      return;
    }

    if (initializedRef.current) {
      return;
    }
    initializedRef.current = true;

    supabase.auth.getSession().then(async ({ data: { session: initialSession }, error: sessionError }) => {
      // Check localStorage for stored auth data
      const storedAuth = localStorage.getItem('myFitBeats-auth');
      
      // If no session but we have stored auth data, try to recover
      if (!initialSession && storedAuth && !sessionError) {
        try {
          const parsed = JSON.parse(storedAuth);
          if (parsed.refresh_token) {
            const { data: refreshData, error: refreshError } = await supabase.auth.refreshSession({
              refresh_token: parsed.refresh_token
            });
            if (!refreshError && refreshData.session) {
              setSession(refreshData.session);
              setInitializing(false);
              return;
            } else {
              console.warn('[auth] Session recovery failed:', refreshError?.message);
              // Clear stale auth data
              localStorage.removeItem('myFitBeats-auth');
            }
          }
        } catch (e) {
          console.warn('[auth] Failed to parse stored auth:', e);
        }
      }
      
      // Check "Remember Me" preference (simplified - always remember by default)
      const dontRememberMe = localStorage.getItem('myFitBeats_dontRememberMe');
      const rememberMePref = sessionStorage.getItem('myFitBeats_rememberMe');
      
      if (initialSession && dontRememberMe === 'true' && rememberMePref === null) {
        // User explicitly chose "don't remember me" and this is a new browser session
        await supabase.auth.signOut();
        localStorage.removeItem('myFitBeats_dontRememberMe');
        localStorage.removeItem('myFitBeats-auth');
        setSession(null);
        setInitializing(false);
        return;
      }
      
      // Mark this session as active
      if (initialSession) {
        sessionStorage.setItem('myFitBeats_rememberMe', 'true');
      }
      
      setSession(initialSession);
      setInitializing(false);
    });

    // Track if we've already handled SIGNED_IN to prevent multiple invalidations
    let hasHandledSignIn = false;
    
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, newSession) => {
      
      // CRITICAL: Don't set session to null during token refresh
      // Supabase may fire events with null session during refresh cycles
      // Only set session to null on explicit SIGNED_OUT
      if (newSession) {
        setSession(newSession);
      } else if (event === 'SIGNED_OUT') {
        setSession(null);
        hasHandledSignIn = false; // Reset for next sign in
      }
      // For other events with null session (like transient errors), keep existing session
      
      // Only invalidate queries on explicit SIGNED_IN (user login action)
      // Do NOT invalidate on TOKEN_REFRESHED - that's a background operation
      // and would cause unnecessary refetches that might fail during page load
      // Use flag to prevent multiple invalidations from repeated SIGNED_IN events
      if (event === 'SIGNED_IN' && !hasHandledSignIn) {
        hasHandledSignIn = true;
        // Small delay to ensure session is fully established before refetching
        setTimeout(() => {
          queryClient.invalidateQueries();
        }, 100);
      }
      
      if (event === 'SIGNED_OUT') {
        // Check if we should skip clearing localStorage (when sync failed)
        if (skipLocalStorageClearOnSignOut) {
          skipLocalStorageClearOnSignOut = false; // Reset for next time
          console.log('Skipping localStorage clear - preserving unsynced data for next login');
          queryClient.clear();
          sessionStorage.removeItem('myFitBeats_rememberMe');
          return;
        }
        
        // Clear all user-specific data to prevent showing stale data on next login
        clearUserLocalStorageKeys();
        queryClient.clear();
        sessionStorage.removeItem('myFitBeats_rememberMe');
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [queryClient]);

  // Clear cache when user ID changes (switching accounts)
  useEffect(() => {
    const currentUserId = session?.user?.id || null;
    
    // If we had a previous user and now have a different user, clear all cached data
    if (previousUserIdRef.current !== null && currentUserId !== null && previousUserIdRef.current !== currentUserId) {
      queryClient.clear();
      clearUserLocalStorageKeys();
    }
    
    // Update the tracked user ID
    previousUserIdRef.current = currentUserId;
  }, [session?.user?.id, queryClient]);

  // Get cached user from localStorage for instant display
  const getCachedUser = (): AppUser | null => {
    try {
      const cached = localStorage.getItem('myFitBeats_cachedUser');
      if (cached) return JSON.parse(cached);
    } catch {}
    return null;
  };

  const { data: user, isLoading: isUserLoading, isFetched: isUserFetched, isPending: isUserPending } = useQuery<AppUser | null>({
    queryKey: ["/api/auth/user"],
    enabled: !!session && !initializing,
    retry: 2,
    retryDelay: 1000,
    staleTime: 1000 * 60 * 5,
    placeholderData: (prev) => prev ?? getCachedUser(),
    // Custom queryFn that preserves cached user on 401 instead of returning null
    // This prevents the "data flash then clear" issue on page refresh
    queryFn: async () => {
      const token = session?.access_token;
      const headers: Record<string, string> = {};
      if (token) {
        headers["Authorization"] = `Bearer ${token}`;
      }
      
      const res = await fetch('/api/auth/user', { headers });
      
      if (res.ok) {
        return await res.json();
      }
      
      if (res.status === 401 || res.status === 403) {
        console.warn('[auth] API returned', res.status, '- attempting session refresh');
        
        // Try to refresh the session - but DON'T auto sign-out on failure
        // Let the user stay "logged in" with cached data rather than force logout
        try {
          const { data: refreshData, error: refreshError } = await supabase!.auth.refreshSession();
          
          if (refreshError) {
            // Only sign out for truly fatal auth errors
            if (refreshError.message?.includes('session_not_found') || 
                refreshError.message?.includes('invalid_grant') ||
                refreshError.message?.includes('expired')) {
              console.error('[auth] Fatal auth error, signing out:', refreshError.message);
              await supabase!.auth.signOut();
              return null;
            }
            // For other errors (network, timeout), just log and return null without signing out
            console.warn('[auth] Session refresh failed (non-fatal):', refreshError.message);
            return null;
          }
          
          if (refreshData.session?.access_token) {
            const retryRes = await fetch('/api/auth/user', {
              headers: { "Authorization": `Bearer ${refreshData.session.access_token}` },
            });
            if (retryRes.ok) {
              return await retryRes.json();
            }
            // Retry failed but don't sign out - might be a temporary server issue
            console.warn('[auth] Retry failed, but keeping session');
            return null;
          }
        } catch (e) {
          console.warn('[auth] Session refresh threw error:', e);
          // Don't sign out on exceptions - might be network issue
          return null;
        }
        
        return null;
      }
      
      throw new Error(`${res.status}: ${res.statusText}`);
    },
  });

  // Cache user data for faster loads (with timestamp for initialDataUpdatedAt)
  useEffect(() => {
    if (user) {
      localStorage.setItem('myFitBeats_cachedUser', JSON.stringify(user));
      localStorage.setItem('myFitBeats_cachedUser_timestamp', String(Date.now()));
    }
  }, [user]);

  // Alias for consistency - uses the consolidated helper
  const clearUserLocalData = clearUserLocalStorageKeys;

  const signUp = async (email: string, password: string, firstName?: string, lastName?: string) => {
    if (!supabase) throw new Error("Auth not configured");
    
    clearUserLocalData();
    
    const isProduction = window.location.hostname === 'myfitbeats.app';
    const redirectUrl = isProduction 
      ? 'https://myfitbeats.app/auth/callback'
      : `${window.location.origin}/auth/callback`;
    
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          first_name: firstName,
          last_name: lastName,
        },
        emailRedirectTo: redirectUrl,
      },
    });
    if (error) throw error;
    return data;
  };

  const signIn = async (email: string, password: string, rememberMe: boolean = true) => {
    if (!supabase) throw new Error("Auth not configured");
    
    const previousUserId = localStorage.getItem('myFitBeats_currentUserId');
    
    // Clear ALL cached queries before signing in to prevent showing old user's data
    // This is critical for security and UX - prevents flash of previous user's content
    queryClient.clear();
    
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    if (error) {
      throw error;
    }
    
    const newUserId = data.user?.id;
    if (newUserId && previousUserId && previousUserId !== newUserId) {
      clearUserLocalData();
    }
    if (newUserId) {
      localStorage.setItem('myFitBeats_currentUserId', newUserId);
    }
    
    // Handle "Remember Me" preference - default to always remember
    sessionStorage.setItem('myFitBeats_rememberMe', 'true');
    localStorage.removeItem('myFitBeats_dontRememberMe');
    
    // Wait for user data to be fetched before returning
    await queryClient.invalidateQueries({ queryKey: ["/api/auth/user"] });
    await queryClient.refetchQueries({ queryKey: ["/api/auth/user"], type: "all" });
    
    return data;
  };

  const signOut = async () => {
    if (!supabase) throw new Error("Auth not configured");
    
    // Sync any pending offline data with a timeout to prevent hanging
    const pendingQueue = getOfflineQueue();
    if (pendingQueue.length > 0) {
      console.log(`Syncing ${pendingQueue.length} pending changes before logout...`);
      try {
        // Add 5 second timeout to prevent sync from blocking logout
        const syncPromise = syncQueuedMutations();
        const timeoutPromise = new Promise<{ success: false; syncedCount: 0; failedCount: 0; errors: string[] }>((resolve) => 
          setTimeout(() => resolve({ success: false, syncedCount: 0, failedCount: 0, errors: ['Sync timeout'] }), 5000)
        );
        
        const syncResult = await Promise.race([syncPromise, timeoutPromise]);
        if (!syncResult.success) {
          const remainingQueue = getOfflineQueue();
          if (remainingQueue.length > 0) {
            console.warn(`Sync incomplete: ${remainingQueue.length} items remaining. Data preserved for next session.`);
            skipLocalStorageClearOnSignOut = true;
          }
        }
      } catch (syncError) {
        console.warn('Failed to sync pending data before logout:', syncError);
        skipLocalStorageClearOnSignOut = true;
      }
    }
    
    // Always proceed with logout regardless of sync result
    if (!skipLocalStorageClearOnSignOut) {
      clearUserLocalData();
    }
    localStorage.removeItem('myFitBeats_currentUserId');
    queryClient.clear();
    
    try {
      await supabase.auth.signOut();
    } catch (error) {
      console.warn('Supabase signOut error:', error);
    }
    
    // Always redirect to home page
    window.location.replace("/");
  };

  const signInWithGoogle = async () => {
    if (!supabase) throw new Error("Auth not configured");
    
    // Clear ALL cached queries before OAuth redirect to prevent showing old user's data
    queryClient.clear();
    clearUserLocalData();
    // Clear any previous "don't remember" preference for OAuth logins
    localStorage.removeItem('myFitBeats_dontRememberMe');
    
    const isProduction = window.location.hostname === 'myfitbeats.app';
    const redirectUrl = isProduction 
      ? 'https://myfitbeats.app/auth/callback'
      : `${window.location.origin}/auth/callback`;
    
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: redirectUrl,
        queryParams: {
          access_type: 'offline',
          prompt: 'consent',
        },
      },
    });
    if (error) throw error;
    return data;
  };

  const resetPassword = async (email: string) => {
    if (!supabase) throw new Error("Auth not configured");
    
    const isProduction = window.location.hostname === 'myfitbeats.app';
    const redirectUrl = isProduction 
      ? 'https://myfitbeats.app/auth/reset-password'
      : `${window.location.origin}/auth/reset-password`;
    
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: redirectUrl,
    });
    if (error) throw error;
  };

  const updatePassword = async (newPassword: string) => {
    if (!supabase) throw new Error("Auth not configured");
    
    const { error } = await supabase.auth.updateUser({
      password: newPassword,
    });
    if (error) throw error;
  };

  // isLoading is true only during initial session restoration
  // If we have cached user data, skip the loading screen for faster perceived performance
  const hasCachedUser = !!getCachedUser();
  const isLoading = initializing || (!!session && isUserLoading && !hasCachedUser);
  
  // User is authenticated if they have a valid Supabase session
  // User data (profile) is fetched separately and may load after initial render
  const isAuthenticated = !!session;
  
  // isUserReady indicates that the user data has been fetched from the API (not just cached)
  // Use this to gate queries that need confirmed authentication
  const isUserReady = isUserFetched && !!user;

  return {
    user,
    session,
    isLoading,
    isAuthenticated,
    isUserReady,
    signUp,
    signIn,
    signInWithGoogle,
    signOut,
    resetPassword,
    updatePassword,
  };
}
